import type { SuperHubStore } from '..';
import type { ReadMergeType } from '@readme/api/src/mappings/merge/types';
import type { SWRConfiguration } from 'swr';

import React, { useEffect, useMemo } from 'react';

import useEnvInfo from '@core/hooks/useEnvInfo';
import useReadmeApi from '@core/hooks/useReadmeApi';
import type { APIErrorType } from '@core/types';

import { useSuperHubStore } from '..';

/** Default SWR options for our diff status fetches. */
const swrOptions: SWRConfiguration = {
  revalidateOnFocus: false,
  shouldRetryOnError: false,
};

/**
 * Helper function used to stagger our `useSWR` hook calls by accepting a SWR
 * return value as the previous SWR hook to wait for. When the provided SWR
 * value is finished loading, returns the `url` string that is then used to
 * initialize the next hook.
 * @example
 * ```ts
 * const A = useReadmeApi(waitFor(null, '/merge'));
 * const B = useReadmeApi(waitFor(A, '/merge'));
 * const C = useReadmeApi(waitFor(B, '/merge'));
 * const D = useReadmeApi(waitFor(D, '/merge'));
 * ```
 */
function waitFor(
  swrData: ReturnType<typeof useReadmeApi<ReadMergeType<'expanded'>, APIErrorType>> | null,
  url: string,
) {
  if (!swrData) return url;
  return !swrData.isLoading && (swrData.data || swrData.error) ? url : null;
}

interface InitializeBranchDiffStatusProps {
  children: React.ReactNode;
  initialDiffStatusMap?: SuperHubStore['branches']['diffStatusMap'];
}

/**
 * Initializes the current branch's diff statuses with initial data and connects
 * to the merge status endpoint to continually keep diff data in sync.
 */
export function InitializeDiffStatus({ children, initialDiffStatusMap }: InitializeBranchDiffStatusProps) {
  const { isServer } = useEnvInfo();
  const [getApiBaseUrl, updateDiffStatusMap] = useSuperHubStore(s => [s.getApiBaseUrl, s.branches.updateDiffStatusMap]);

  // PART 1: initialize our store during SSR and first CSR render if/when we
  // have initial versions data included in our SSR props generated by our
  // express controller. We must call this inline rather than inside an effect.
  if (isServer && initialDiffStatusMap) {
    updateDiffStatusMap(initialDiffStatusMap);
  }

  const apiBaseUrl = useMemo(() => getApiBaseUrl(), [getApiBaseUrl]);
  const swrGuides = useReadmeApi<ReadMergeType<'expanded'>, APIErrorType>(
    waitFor(null, '/merge?format=expanded&section=docs'),
    {
      apiBaseUrl,
      swr: swrOptions,
    },
  );

  const swrRecipes = useReadmeApi<ReadMergeType<'expanded'>, APIErrorType>(
    waitFor(swrGuides, '/merge?format=expanded&section=recipes'),
    { apiBaseUrl, swr: swrOptions },
  );

  const swrReference = useReadmeApi<ReadMergeType<'expanded'>, APIErrorType>(
    waitFor(swrRecipes, '/merge?format=expanded&section=reference'),
    { apiBaseUrl, swr: swrOptions },
  );

  const swrCustomPages = useReadmeApi<ReadMergeType<'expanded'>, APIErrorType>(
    waitFor(swrReference, '/merge?format=expanded&section=custom_pages'),
    { apiBaseUrl, swr: swrOptions },
  );

  const swrCustomBlocks = useReadmeApi<ReadMergeType<'expanded'>, APIErrorType>(
    waitFor(swrCustomPages, '/merge?format=expanded&section=custom_blocks'),
    { apiBaseUrl, swr: swrOptions },
  );

  // Update all SWR load statuses
  useEffect(() => {
    updateDiffStatusMap({
      guides: {
        data: swrGuides.data?.data || null,
        error: swrGuides.error || null,
        isLoading: swrGuides.isLoading,
        swrKey: swrGuides.swrKey,
      },
      recipes: {
        data: swrRecipes.data?.data || null,
        error: swrRecipes.error || null,
        isLoading: swrRecipes.isLoading,
        swrKey: swrRecipes.swrKey,
      },
      reference: {
        data: swrReference.data?.data || null,
        error: swrReference.error || null,
        isLoading: swrReference.isLoading,
        swrKey: swrReference.swrKey,
      },
      customPages: {
        data: swrCustomPages.data?.data || null,
        error: swrCustomPages.error || null,
        isLoading: swrCustomPages.isLoading,
        swrKey: swrCustomPages.swrKey,
      },
      customContent: {
        data: swrCustomBlocks.data?.data || null,
        error: swrCustomBlocks.error || null,
        isLoading: swrCustomBlocks.isLoading,
        swrKey: swrCustomBlocks.swrKey,
      },
    });
  }, [swrGuides, swrRecipes, swrReference, swrCustomPages, swrCustomBlocks, updateDiffStatusMap]);

  return <>{children}</>;
}
